import { ICourse2 } from "@et/types/Courses";
import { PAYMENT_TYPES_THEME } from "@et/types/Enums";
import { neonPink } from "@styles/themes/darkThemeBeautifulLettering";
import { graphql } from "gatsby";
const slug = 'mixed-media-procreate'
const title = 'Mixed Media Florals in Procreate'
const mainColor = `#4D286F`

const course: ICourse2 = {
  theme: PAYMENT_TYPES_THEME.LIGHT,
  title,
  slug,
  videoId: `5fxpd7v7cx`,
  buyNowPayLater: null,
  keywords: [
    'mixed media florals in Procreate',
    'Procreate flower painting course',
    'Procreate watercolor flowers',
    'mixed media art Procreate',
    'floral illustration Procreate',
    'Procreate mixed media brushes',
    'how to paint flowers in Procreate',
    'Procreate painting techniques',
    'Procreate for floral design',
    'digital watercolor Procreate',
    'Procreate texture techniques',
    'mixed media techniques Procreate',
    'expressive florals in Procreate',
    'Procreate leaf painting',
    'Procreate bouquet painting',
    'Procreate brush techniques',
    'digital art flower course',
    'Procreate art tutorials',
    'printable art Procreate',
    'Procreate wall art course',
    'art licensing Procreate',
    'Procreate POD course',
    'print on demand for artists',
    'Procreate for Etsy',
    'Procreate Gelato setup',
    'how to sell Procreate art',
    'Procreate for passive income',
    'Procreate floral bouquet',
    'Procreate painterly techniques',
    'Procreate creative process',
    'Procreate textures',
    'Procreate gradient maps',
    'digital mixed media painting',
    'Procreate fine art prints',
    'Procreate floral compositions',
    'create and sell Procreate art',
    'Procreate acrylic painting',
    'Procreate messy painting techniques'
  ],
  price: '297',
  salePrice: '247',
  paymentPlan: {
    price: '80',
    perMonth: '4 Payments of $80/m',
    savings: '10%',
    salePrice: '85',
  },
  motionMail: `https://s.mmgo.io/t/C7Bp/scale_2x`,
  onSale: false,
  tagline: 'Create beautiful stationery designs in Procreate!',
  classDescriptor: 'Create beautiful stationery designs in Procreate!',
  intro: {
    imgName: 'procreate301/tablet-intro.png',
    imgAlt: 'Create custom modern lettering styles that are unique to you',
    bullets: [
      {
        title: 'Beginner Friendly',
        desc:
          'Step-by-step narration with closeups on all the details, so you feel confident in the process from start to finish.',
      },
      {
        title: 'A Versatile Process',
        desc:
          'Since every lettering style is different, this process has been fine-tuned to fit any situation. You’ll be able to achieve a realistic watercolor effect when you apply it to any style - hand lettered or typed.',
      },
      {
        title: 'Blend Options',
        desc:
          'You may want an ombré, one color, smooth transitions blend with one of your pieces and a multi-colored blend with water pockets on another. This course serves up 5 different blend options, so you’ll have plenty to choose from when you begin your next lettering piece.',
      },
      {
        title: 'Florals + Foliage Integration',
        desc:
          'Take your watercolor lettering even further by integrating florals and foliage! A bonus florals foundations module is included, so even if you’ve never painted florals before, you’ll be able to follow along and complete every project in the class.',
      },
    ],
  },
  projects: {
    beginner: [
      {
        title: 'Project 1',
        backgroundColor: `#c4d8da`,
        textColor: `#4a5457`,
        subheadColor: `#fff`,
        projectImg: `procreate301/project-1.jpg`,
        projectImgMobile: `procreate301/project-1.jpg`,
        type: '',
        desc:
          'We kick things off with a seashell symmetry full drop pattern. We\'ll set up our template together using the symmetry block brushes, paint in the elements, then make all edges seamless through pre-programmed selections. Once we have all of our gaps filled in, we\'ll drag it into the Pattern Playground for a final test.',
      },
      {
        title: 'Project 2',
        backgroundColor: `#fcbfb2`,
        textColor: `#4a5457`,
        subheadColor: `#fcb900`,
        projectImg: `procreate301/project-2.jpg`,
        projectImgMobile: `procreate301/project-2-mobile.jpg`,
        type: '',
        desc:
          'To reinforce what we covered in Project 1, we\'ll use another symmetry block brush - this time the flower shape, and create another full drop pattern. I share how I fix troublesome areas when elements moving in different directions collide and I explain my full thought process along with my reasons behind every decision I make. We reconfigure our pattern to make it seamless using our pre-programmed selections and do a final test using the Pattern Playground.',
      },
      {
        title: 'Project 3',
        backgroundColor: `#ffe3df`,
        textColor: `#4a5457`,
        subheadColor: `#f3ecdf`,
        projectImg: `procreate301/project-3.jpg`,
        projectImgMobile: `procreate301/project-3-mobile.jpg`,
        type: '',
        desc: 'In Project 3, we create a full drop repeat using an included composition template. Following the composition template, we\'re able to speed up our workflow and quickly adjust and adapt it to fit our needs. Once our elements are in place, we use our pre-programmed selections within the template to fill in the gaps, then test + adjust as needed using the Pattern Playground.',
      },
      {
        title: 'Project 4',
        projectImg: `procreate501/project-4.jpg`,
        projectImgMobile: `procreate301/project-4-mobile.jpg`,
        backgroundColor: `#dee5dd`,
        textColor: `#565857`,
        subheadColor: `#c56b00`,
        type: '',
        desc:
          'It\'s time to head into half drop patterns in Project 4! We\'ll work with a different composition template this time, pre-programmed with selections and set up specifically for half drops. After painting our elements in, we\'ll reorganize the template to fill in gaps using our selections, then test + adjust using the Pattern Playground.',
      },
      {
        title: 'Project 5',
        projectImg: `procreate301/project-5.jpg`,
        projectImgMobile: `procreate301/project-5-mobile.jpg`,
        backgroundColor: `#ffd5bc`,
        textColor: `#4a5457`,
        subheadColor: `#419187`,
        type: '',
        desc:
          'To round out our pattern types, Project 5 is all about the half brick. We\'ll pick a half brick specific composition template, lay in our artwork and go off script from the template as needed. We then reorganize our artwork using the pre-programmed selections to fill in gaps and test our completed half brick within the Pattern Playground.',
      },
    ],
    advanced: [],
  },
  curriculum: [
    {
      title: 'Welcome + Bonuses',
      videos: [
        {
          title: 'What You\'ll Learn/ Class Overview',
          duration: '9:33',
        },
        {
          title: 'Bonuses Location/Install Instructions',
          duration: '8:09',
        },
      ],
    },
    {
      title: 'Getting Started',
      videos: [
        {
          title: 'Mixed Media Lovers Brushes/Canvas Overview',
          duration: '11:43',
        },
        {
          title: 'Handy Gestures',
          duration: '6:50',
        },
        {
          title: 'Using Source Photos',
          duration: '5:32',
        },
        {
          title: 'File Setup Tips',
          duration: '3:12',
        },
        {
          title: 'Easy Photo Enhancements within Procreate',
          duration: '9:24',
        },
      ]
    },
    {
      title: 'The Mixed Media Style',
      videos: [
        {
          title: 'Traditional Mixed Media Style Examples',
          duration: '5:56',
        },
      ],
    },
    {
      title: 'Project 1: Graphic Style Leaves',
      videos: [
        {
          title: 'Prepare Your Canvas',
          duration: '9:08',
        },
        {
          title: 'Create Isolation Gradient Maps',
          duration: '5:52',
        },
        {
          title: 'Paint Shadows + Midtones',
          duration: '15:47',
        },
        {
          title: 'Paint Highlights',
          duration: '4:08',
        },
        {
          title: 'Enhance Leaves with Extra Texture',
          duration: '13:06',
        },
        {
          title: 'Add Outline + Background',
          duration: '11:11',
        },
      ],
    },
    {
      title: 'Project 2: Graphic Style Floral',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '7:29',
        },
        {
          title: 'Create Gradient Maps + Paint Shadows',
          duration: '10:07',
        },
        {
          title: 'Paint Base Color',
          duration: '13:32',
        },
        {
          title: 'Paint Highlights',
          duration: '3:26',
        },
        {
          title: 'Enhance with Texture',
          duration: '6:57',
        },
        {
          title: 'Add Outline + Background',
          duration: '6:29',
        },
      ],
    },
    {
      title: 'Project 3: Graphic Style Bouquet',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '8:55',
        },
        {
          title: 'Create Gradient Maps',
          duration: '4:23',
        },
        {
          title: 'Paint Shadows',
          duration: '16:14',
        },
        {
          title: 'Paint Base Color Part 1',
          duration: '17:15',
        },
        {
          title: 'Paint Base Color Part 2',
          duration: '13:27',
        },
        {
          title: 'Paint Highlights',
          duration: '7:32',
        },
        {
          title: 'Enhance with Texture',
          duration: '7:53',
        },
        {
          title: 'Add Final Details',
          duration: '6:30',
        },
        {
          title: 'Add Outline + Background',
          duration: '9:00',
        },
      ]
    },
    {
      title: 'Project 4: Semi-Realistic Leaves',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '4:58',
        },
        {
          title: 'Paint Shadows',
          duration: '4:57',
        },
        {
          title: 'Paint Base Watercolor',
          duration: '15:14',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '5:51',
        },
        {
          title: 'Paint Hightlights',
          duration: '4:50',
        },
        {
          title: 'Paint Final Details + Background',
          duration: '5:52',
        },
      ]
    },
    {
      title: 'Project 5: Semi-Realistic Floral',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '10:41',
        },
        {
          title: 'Paint Shadows',
          duration: '7:17',
        },
        {
          title: 'Paint Base Watercolor: Petals',
          duration: '14:22',
        },
        {
          title: 'Watercolor Blending: Petals',
          duration: '8:53',
        },
        {
          title: 'Paint Base Watercolor: Stamen',
          duration: '7:23',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '7:50',
        },
        {
          title: 'Paint Highlights',
          duration: '7:32',
        },
        {
          title: 'Paint Final Details + Background',
          duration: '10:58',
        }
      ]
    },
    {
      title: 'Project 6: Semi-Realistic Bouquet',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '9:54',
        },
        {
          title: 'Paint Shadows',
          duration: '11:37',
        },
        {
          title: 'Paint Base Watercolor: Florals',
          duration: '18:41',
        },
        {
          title: 'Watercolor Blending: Florals',
          duration: '9:37',
        },
        {
          title: 'Paint Base Watercolor: Foliage',
          duration: '9:59',
        },
        {
          title: 'Paint Base Watercolor: Vase',
          duration: '8:14',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '8:55',
        },
        {
          title: 'Paint Highlights',
          duration: '12:57',
        },
        {
          title: 'Paint Final Details',
          duration: '11:34',
        },
        {
          title: 'Paint Background',
          duration: '17:23',
        },
      ]
    },
    {
      title: 'Project 7: Messy Abstract Leaves',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '4:42',
        },
        {
          title: 'Paint Shadows',
          duration: '4:24',
        },
        {
          title: 'Paint Base Watercolor',
          duration: '11:32',
        },
        {
          title: 'Paint Highlights',
          duration: '3:21',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '3:53',
        },
        {
          title: 'Paint Final Details + Background',
          duration: '10:13',
        },
      ]
    },
    {
      title: 'Project 8: Messy Abstract Floral',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '6:22',
        },
        {
          title: 'Paint Shadows',
          duration: '8:51',
        },
        {
          title: 'Paint Base Watercolor',
          duration: '5:30',
        },
        {
          title: 'Blend Watercolor + Add Messiness',
          duration: '9:33',
        },
        {
          title: 'Paint Highlights',
          duration: '6:57',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '14:10',
        },
        {
          title: 'Paint Final Details + Background',
          duration: '15:55',
        },

      ]
    },
    {
      title: 'Project 9: Messy Abstract Bouquet',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '6:13',
        },
        {
          title: 'Paint Shadows',
          duration: '13:18',
        },
        {
          title: 'Smudge Shadows',
          duration: '6:28',
        },
        {
          title: 'Paint Base Watercolor',
          duration: '5:35',
        },
        {
          title: 'Blend Watercolor + Add Messiness',
          duration: '10:58',
        },
        {
          title: 'Paint Highlights',
          duration: '14:51',
        },
        {
          title: 'Smudge Highlights',
          duration: '7:41',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '9:21',
        },
        {
          title: 'Add Final Details: Part 1',
          duration: '19:01',
        },
        {
          title: 'Add Final Details: Part 2',
          duration: '13:07',
        },
        {
          title: 'Finalize Hero Floral + Paint Background',
          duration: '14:39',
        },
      ]
    },
    {
      title: 'Bonus: [Project 10] Mixed Media Owl',
      videos: [
        {
          title: 'Prepare the Canvas',
          duration: '5:17',
        },
        {
          title: 'Paint Shadows',
          duration: '9:17',
        },
        {
          title: 'Smudge Shadows',
          duration: '3:21',
        },
        {
          title: 'Paint Base Watercolor',
          duration: '4:22',
        },
        {
          title: 'Blend Base Watercolor',
          duration: '4:59',
        },
        {
          title: 'Add Messiness',
          duration: '2:24',
        },
        {
          title: 'Paint Eyes',
          duration: '22:00',
        },
        {
          title: 'Add Texture Enhancements',
          duration: '11:25',
        },
        {
          title: 'Paint Highlights',
          duration: '5:34',
        },
        {
          title: 'Smudge Highlights',
          duration: '7:31',
        },
        {
          title: 'Paint Final Details + Background',
          duration: '18:00',
        },
      ]
    },
    {
      title: 'Bonus: Monetizing Mixed Media with Print On Demand Art Prints',
      videos: [
        {
          title: 'Print on Demand Overview',
          duration: '4:45',
        },
        {
          title: 'POD Platforms Pros + Cons',
          duration: '4:44',
        },
        {
          title: 'Gelato Overview',
          duration: '3:40',
        },
        {
          title: 'Preparing Your Files',
          duration: '4:06',
        },
        {
          title: 'Uploading Your Files',
          duration: '3:27',
        },
        {
          title: 'Ordering Process',
          duration: '2:43',
        },
        {
          title: 'My Experience/Example Prints',
          duration: '13:34',
        },
        {
          title: 'Setting Up a Storefront + Establishing Pricing',
          duration: '4:07',
        },
      ]
    },
    {
      title: 'BONUS: Repurposing Your Artwork',
      videos: [
        {
          title: 'How to Convert Your Artwork to CMYK',
          duration: '4:44',
        },
        {
          title: 'How to Save Your Artwork with a Transparent Background',
          duration: '5:42',
        },
        {
          title: 'Enlarging Your Artwork (what are your options?)',
          duration: '12:56',
        },
      ]
    },
    {
      title: 'Next steps',
      videos: [
        {
          title: 'Thank You',
          duration: '4:44',
        },
      ],
    },
  ],
  bonuses: {
    headerImgName: `procreate301/bonus-title.png`,
    imgAlt: 'Messy Watercolors in Procreate Bonuses',
    gridType: 1,
    items: [
      {
        title: 'Mixed Media Lovers Brush Set + Extended License',
        desc: '<p>You’ll get the full brush set used throughout the course–over 100 brushes, including painting, smudging, texture stamps and watercolor washes–ready to use the moment you enroll. An extended license is also included, so you can use the brushes in artwork you plan to sell, without limitation.</p>',
        imgName: 'mixed-media/bonuses/bonus-1.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Mixed Media Style Cheat Sheets',
        desc: '<p>Each of the 3 course styles comes with a ready to print PDF cheat sheet so you can revisit the process anytime. They’re perfect for quick reference or refreshing your memory when creating your own artwork in the future.</p>',
        imgName: 'mixed-media/bonuses/bonus-2.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Helpful Links',
        desc: '<p>This organized, clickable resource includes everything mentioned throughout the course–free and paid photo sites, POD platforms, image upscalers, iPad accessories, book recs, curated Pinterest boards and more–all vetted and recommended from personal experience.</p>',
        imgName: 'mixed-media/bonuses/bonus-3.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: "Private Procreate Community",
        desc: '<p>We’ve moved off Facebook and into our own private space –right inside Teachable–so you can share your work and connect without distractions. It’s the perfect place to find answers to questions, receive feedback and a non stop source of support from the best Procreate makers around. I’m always there, too!</p>',
        imgName: 'mixed-media/bonuses/bonus-4.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Project 10: Mixed Media Owl',
        desc: '<p>Learn how to adapt the mixed media processes covered in the course to any subject! In this bonus project, we’ll paint a semi-realistic owl using a combination of soft and messy techniques–focusing on realistic eyes, layered textures and mixed media details that bring it to life.</p>',
        imgName: 'mixed-media/bonuses/bonus-5.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Monetizing Mixed Media with POD',
        desc: '<p>Learn how to take your mixed media Procreate artwork from digital to physical with print on demand! We’ll walk through file prep, ordering test prints, uploading artwork and setting up a storefront using Gelato–plus how to price your work with confidence and sell it on platforms like Etsy and Shopify.</p>',
        imgName: 'mixed-media/bonuses/bonus-6.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'How to Convert Your Artwork to CMYK',
        desc: '<p>You’ll learn how to take any artwork created in RGB and convert it into a flattened *and* layered CMYK file, all within Procreate. This allows you to retain the expansive color gamut of RGB for any on screen or modern print applications, while still having the flexibility to offer CMYK files if ever needed.</p>',
        imgName: 'mixed-media/bonuses/bonus-7.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'How to Save Your Artwork with Transparent Backgrounds',
        desc: '<p>You’ll learn how to cleanly remove background color while keeping all of your textures intact–perfect for reusable artwork. You’ll also learn how to add an isolated color layer beneath watercolor to pull it forward (and make it less faded) when used on a transparent background!</p>',
        imgName: 'mixed-media/bonuses/bonus-8.jpg',
        bgColor: '#FFF6EE'
      },
      {
        title: 'Enlarging Your Artwork',
        desc: '<p>Need your artwork at a bigger size than it was originally created? This lesson walks you through 3 enlargement methods (and when to use each one), so you can scale your artwork up for any scenario–without ever starting over.</p>',
        imgName: 'mixed-media/bonuses/bonus-9.jpg',
        bgColor: '#FFF6EE'
      },
    ],
  },
  courseCard: {
    title: 'Messy Watercolors in Procreate',
    backgroundImgName: ``,
    backgroundColor: '#65807c',
    cardImgName: `procreate301/watercolor-class-card-4.jpg`,
    desc: 'Paint vibrant and messy eye candy watercolor artwork in Procreate! Reference traditional methods while utilizing digital advantages like blend modes, selective color adjustments and layers. Follow along as each project builds in complexity with technique, process and levels of messiness. In addition to painting, you’ll learn and use multiple layers, blend modes, selections, recoloring, saturation adjustments and warping to create stunning artwork using an efficient process. Finish the course by learning how to prepare your artwork for print using a home printer or online print services to enjoy your art beyond the screen.',
    bullets: [

    ],
    brush: {
      enabled: true,
      image: {
        pencil: `apple2-pencil.png`,
        shadow: `sell-card-pencil-reflection-light.png`,
      },
    },
    priceColor: mainColor,
    saleColor: neonPink,
    stats: {
      modules: `#cc836f`,
      videos: `#758384`,
      hours: `#a79e8d`,
    },
  },
  faqs: [
    {
      question: 'What if I have no drawing experience or skills?',
      answer: '<p>This course was created with beginners in mind! The process we use leans on using our reference images as a base that we expand upon, so you’ll have a structure to inject your own creativity into. All steps are explained in detail and shown in real time, so you’ll know the how’s and why’s of everything we do.</p>',
    },
    {
      question: 'I’m brand new to Procreate, will I be able to follow along?',
      answer: '<p>This course was created with beginners in mind, though if you’re *brand* new to Procreate, I recommend taking my <a target="_blank" href="https://learn.every-tuesday.com/procreate-for-beginners">free Procreate for Beginners course</a> first to get familiar with the interface/primary tools before starting.</p>',
    },
    {
      question: 'What do I need to take this course?',
      answer: '<p>You’ll need an iPad that supports pressure sensitivity along with a compatible stylus (<a target="_blank" href="https://support.apple.com/en-us/HT211029">check out this list</a>) and the Procreate app (v.5.3.14 or newer) installed.</p>',
    },
    {
      question: 'I’m not sure I have time for this?',
      answer: '<p>All Every Tuesday courses come with lifetime access, so you can take it at the time and pace that works best for you and your schedule.</p>',
    },
    {
      question: 'Do you offer any refunds?',
      answer: '<p>This course is backed by our 14 day money back guarantee. If you take the course and feel your Procreate painting/mixed media skills and/or knowledge have not benefited from the course, send me an email before the 14 days is up and I’ll issue a full refund.</p>',
    },
    {
      question: 'Does this class come with subtitles/closed captions?',
      answer: '<p>Yes! English subtitles are provided for every video.</p>'
    },
    {
      question: 'How long do I get access?',
      answer: '<p>You buy something, you get to keep it forever around here.</p>',
    },
    {
      question: 'Can I sell the class projects I recreate as my own?',
      answer: '<p>Course projects that use Unsplash images as reference images (projects 5, 7 and 10) may be recreated and sold, though my hope is that these processes, methods and techniques are used to create your own pieces from reference images that are meaningful to you.</p>',
    },
    {
      question: 'I have more questions, how can I get in touch?',
      answer: '<p>Head to <a target="_blank" href="https://every-tuesday.com/contact">my contact page</a> and send em over! I’m just an email away.</p>',
    },
  ],
  testimonials: [],
  letsDoThis: {
    imgName: 'procreate301/lets-do-this-2.jpg',
    altTag: 'Enroll in Messy Watercolors for Procreate today!',
    text: 'Welcome to Flat Florals in Procreate, your complete guide to painting simplified, bold and beautiful florals.',
  },
  sellCard: {
    priceColor: '#5f1212',
    saleColor: neonPink,
    cardImg: '',
    link: 'https://courses.every-tuesday.com/purchase?product_id=6218055',
    saleLink: 'https://courses.every-tuesday.com/purchase?product_id=6218060',
    paymentPlanLink: 'https://courses.every-tuesday.com/purchase?product_id=6232827',
    paymentPlanSaleLink: 'https://courses.every-tuesday.com/purchase?product_id=6218059',
    customBtnBackgroundColor: 'var(--ff-teal-600)',
    h2Color: `#5f1212`,
    hoverColor: {
      h2: `#fff`,
      text: 'var(--ff-teal-600)',
      overlay: `var(--ff-teal-400)`,
      cardDescription: '#fff'
    },
    bullets: [
      'Instant access to all videos and bonuses',
      'Full Bouquet Maker brush set (150+ brushes)',
      'Bonus applications module (3 projects)',
      'Private Facebook community',
      'Extended License of the brush set ($40 value)',
      'Seamless patterns with transparent backgrounds process',
      'Rose simplification tutorial',
      'Composition design tutorials',
      '14 day money back guarantee',
      'Flat style cheat sheets (pdf)',
      'All project + demo color palettes',
      'English subtitles on all videos',
      'Basic + complex style practice exercises (12 total)',
    ],
  },
}
export const gqlFragmentMixedMediaImages = graphql`
    fragment FragmentMixedMediaImages on Query {
      classProjectsImg: imgixImage(url: "mixed-media/class-project-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      authorTop: imgixImage(url: "mixed-media/author/author-top-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      authorBot: imgixImage(url: "mixed-media/author/author-bot-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      reqsFeaturedImage: imgixImage(url: "mixed-media/reqs-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 1200)
      }
      project1_bg: imgixImage(url: "mixed-media/projects/project-1-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project1_img: imgixImage(url: "mixed-media/projects/project-1-img-min-v2.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project2_img: imgixImage(url: "mixed-media/projects/project-2-img-min-v3.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project2_bg: imgixImage(url: "mixed-media/projects/project-2-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project3_bg: imgixImage(url: "mixed-media/projects/project-3-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project3_img: imgixImage(url: "mixed-media/projects/project-3-img-min-v2.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project4_img: imgixImage(url: "mixed-media/projects/project-4-img-min-v3.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project4_bg: imgixImage(url: "mixed-media/projects/project-4-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project5_bg: imgixImage(url: "mixed-media/projects/project-5-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project5_img: imgixImage(url: "mixed-media/projects/project-5-img-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project6_bg: imgixImage(url: "mixed-media/projects/project-6-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project6_img: imgixImage(url: "mixed-media/projects/project-6-img-min-v2.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project7_bg: imgixImage(url: "mixed-media/projects/project-7-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project7_img: imgixImage(url: "mixed-media/projects/project-7-img-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project8_bg: imgixImage(url: "mixed-media/projects/project-8-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project8_img: imgixImage(url: "mixed-media/projects/project-8-img-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project9_bg: imgixImage(url: "mixed-media/projects/project-9-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project9_img: imgixImage(url: "mixed-media/projects/project-9-img-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      project10_bg: imgixImage(url: "mixed-media/projects/project-10-bg.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          }, srcSetMaxWidth: 1600)
      }
      project10_img: imgixImage(url: "mixed-media/projects/project-10-img-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
          })
      }
      bonusHero2: imgixImage(url: "mixed-media/bonuses/modules/bonus-3-a-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {}, srcSetMaxWidth: 1200)
      }
      bonusHero4Top: imgixImage(url: "mixed-media/bonuses/modules/bonus-2-a-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {}, srcSetMaxWidth: 1000)
      }
      bonusHero4Bottom: imgixImage(url: "mixed-media/bonuses/modules/bonus-2-b-2.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {}, srcSetMaxWidth: 1000)
      }
      breakOut: imgixImage(url: "mixed-media/breakout-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 1200)
      }
      selfPacedImg: imgixImage(url: "mixed-media/self-paced/self-paced-2-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      playgroundVideo: file(relativePath: { eq: "mixed-media/leaf-gif.mp4"}) {
          publicURL
      }
      playgroundPoster: imgixImage(url: "mixed-media/leaf-gif-poster.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              auto: "compress",
          })
      }
      textureVideo: file(relativePath: { eq: "mixed-media/texture-compilation-compressed-HD.mp4"}) {
          publicURL
      }
      teelaWorking: imgixImage(url: "mixed-media/author/teela-desk-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      teelaWorking2: imgixImage(url: "mixed-media/author/teela-desk-2-2.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      patternHeroBg: imgixImage(url: "mixed-media/hero-header-bg-2.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 2800)
      }
      patternHero: imgixImage(url: "mixed-media/ipad-hero-bg-2-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 2800)
      }
      mmHeroPencil: imgixImage(url: "mixed-media/pencil-flat-1-min.png") {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      mmHeroBrush: imgixImage(url: "mixed-media/brush-flat-1-min.png") {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      titleArt: imgixImage(url: "mixed-media/mm-ipad-title-art.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 1200)
      }
      yellowBag: imgixImage(url: "stationery-design-in-procreate/tote-handbag.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      introImg_1: imgixImage(url: "mixed-media/intro/top-intro-a-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      introImg2: imgixImage(url: "mixed-media/intro/top-intro-b-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      introImg_3: imgixImage(url: "mixed-media/intro/top-intro-c-1-min.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      brushSet_1: imgixImage(url: "mixed-media/brushes/brush-card-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
      preview4: imgixImage(url: "procreate-patterns/preview-4.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
            fit: "crop",
            auto: "compress"
        }, srcSetMaxWidth: 1200)
      },
      ipadFlatLight: imgixImage(url: "ipad-device-flat-light.png") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              w: 1200
          })
      }
      moneyBack: imgixImage(url: "mixed-media/14-day-guarantee-2.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {})
      }
    } 
`

export const gqlFragmentProcreateMixedMediaWMCD_Images = graphql`
    fragment FragmentProcreateMixedMediaWMCD_Images on Query {
        WMCD1:imgixImage(url: "mixed-media/diff/diff-1.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD2:imgixImage(url: "mixed-media/diff/diff-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD3:imgixImage(url: "mixed-media/diff/diff-3.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD4:imgixImage(url: "mixed-media/diff/diff-4.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD5:imgixImage(url: "mixed-media/diff/diff-5.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD6:imgixImage(url: "mixed-media/diff/diff-6.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD7:imgixImage(url: "mixed-media/diff/diff-7.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD8:imgixImage(url: "mixed-media/diff/diff-8.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD9:imgixImage(url: "mixed-media/diff/diff-9.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD10:imgixImage(url: "mixed-media/diff/diff-10.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD11:imgixImage(url: "mixed-media/diff/diff-11.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        },
        WMCD12:imgixImage(url: "mixed-media/diff/diff-12.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }

    }
`
export const gqlFragmentProcreateMixedMediaWYL = graphql`
    fragment FragmentProcreateMixedMediaWYL on Query {
         wylGridImage1:imgixImage(url: "mixed-media/wyl/wyl-1.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage2:imgixImage(url: "mixed-media/wyl/wyl-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage3:imgixImage(url: "mixed-media/wyl/wyl-3.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage4:imgixImage(url: "mixed-media/wyl/wyl-4.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage5:imgixImage(url: "mixed-media/wyl/wyl-6.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage6:imgixImage(url: "mixed-media/wyl/wyl-5.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage7:imgixImage(url: "mixed-media/wyl/wyl-7-2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage8:imgixImage(url: "mixed-media/wyl/wyl-8-v2.jpg") {
             gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage9:imgixImage(url: "mixed-media/wyl/wyl-9.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage10:imgixImage(url: "mixed-media/wyl/wyl-10.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage11:imgixImage(url: "mixed-media/wyl/wyl-11.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylGridImage12:imgixImage(url: "mixed-media/wyl/wyl-12-1.jpg") {
          gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:400,
              auto: "compress"
          }, srcSetMaxWidth: 400)
        }
        wylSquareImage1:imgixImage(url: "mixed-media/wyl/wyl-square-b-1.jpg") {
            gatsbyImageData(width:600, layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
              fit: "crop",
              w:600,
              auto: "compress"
          }, srcSetMaxWidth: 600)
        }
        wylSquareImage2:imgixImage(url: "mixed-media/wyl/wyl-square-a-1.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 800
            })
        }
        wylTall: imgixImage(url: "mixed-media/wyl/wyl-tall.jpg") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
    }
`
export const gqlFragmentProcreateMixedMediaBrushTips = graphql`
    fragment FragmentProcreateMixedMediaBrushTips on Query {
        brushTip_MM_1:imgixImage(url: "mixed-media/brushes/painting/mm-brush-1-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_2:imgixImage(url: "mixed-media/brushes/painting/mm-brush-2-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_3:imgixImage(url: "mixed-media/brushes/painting/mm-brush-3-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_4:imgixImage(url: "mixed-media/brushes/painting/mm-brush-4-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_5:imgixImage(url: "mixed-media/brushes/painting/mm-brush-5-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_6:imgixImage(url: "mixed-media/brushes/painting/mm-brush-6-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_7:imgixImage(url: "mixed-media/brushes/painting/mm-brush-7-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_8:imgixImage(url: "mixed-media/brushes/painting/mm-brush-8-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_9:imgixImage(url: "mixed-media/brushes/painting/mm-brush-9-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_10:imgixImage(url: "mixed-media/brushes/painting/mm-brush-10-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_11:imgixImage(url: "mixed-media/brushes/painting/mm-brush-11-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_12:imgixImage(url: "mixed-media/brushes/painting/mm-brush-12-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_13:imgixImage(url: "mixed-media/brushes/painting/mm-brush-13-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_14:imgixImage(url: "mixed-media/brushes/painting/mm-brush-14-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
        brushTip_MM_15:imgixImage(url: "mixed-media/brushes/painting/mm-brush-15-min.png") {
            gatsbyImageData(layout:FULL_WIDTH, placeholder: BLURRED, imgixParams: {
                w: 500
            })
        }
    }
`

export default course